import React, { useCallback, useMemo, useRef, useState } from 'react';
import { DataTable } from '../components/organisms';
import { Button } from '../components/atoms';
import { DeleteConfirmationModal } from '../components/organisms/DeleteConfirmationModal';
import { CastFormModal } from '../components/organisms/CastFormModal';
import { useCasts, useCastActions } from '../hooks/useCasts';
import type { Column, TableAction } from '../types/table';
import type { Cast } from '../types/api/cast';
import { formatDateForDisplay } from '../types/api/drama';
import { resolveAssetUrl } from '../utils/url';
import toast from 'react-hot-toast';

/**
 * CastManagementPage Component
 *
 * Admin page for managing cast members:
 * - Server-side pagination
 * - Keyword search
 * - CRUD operations (create, edit, delete)
 * - Profile image preview
 * - Nationality & profession labels
 */
export const CastManagementPage: React.FC = () => {
  const { fetchCasts } = useCasts();
  const {
    createCastWithJson,
    createCastWithImage,
    updateCastWithJson,
    updateCastWithImage,
    deleteCast,
  } = useCastActions();
  const refreshKeyRef = useRef(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCast, setSelectedCast] = useState<Cast | null>(null);

  const handleRefresh = useCallback(() => {
    refreshKeyRef.current += 1;
  }, []);

  const handleCreate = useCallback(() => {
    setSelectedCast(null);
    setIsCreateModalOpen(true);
  }, []);

  const handleEdit = useCallback((cast: Cast) => {
    setSelectedCast(cast);
    setIsEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((cast: Cast) => {
    setSelectedCast(cast);
    setIsDeleteModalOpen(true);
  }, []);

  const handleCreateSubmit = useCallback(
    async (data: any, profileImageFile?: File | null) => {
      const payload = {
        name: data.name as string,
        birthDate: data.birthDate as string | undefined,
        nationality: data.nationality as string | undefined,
        role: data.role as string | undefined,
      };

      try {
        if (profileImageFile) {
          await createCastWithImage(payload, profileImageFile);
        } else {
          await createCastWithJson(payload);
        }
        toast.success('Cast created successfully');
        setIsCreateModalOpen(false);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to create cast');
      }
    },
    [createCastWithImage, createCastWithJson, handleRefresh]
  );

  const handleEditSubmit = useCallback(
    async (data: any, profileImageFile?: File | null) => {
      if (!selectedCast) return;

      const payload = {
        name: data.name as string | undefined,
        birthDate: data.birthDate as string | undefined,
        nationality: data.nationality as string | undefined,
        role: data.role as string | undefined,
      };

      try {
        if (profileImageFile) {
          await updateCastWithImage(selectedCast.castId, payload, profileImageFile);
        } else {
          await updateCastWithJson(selectedCast.castId, payload);
        }
        toast.success('Cast updated successfully');
        setIsEditModalOpen(false);
        setSelectedCast(null);
        handleRefresh();
      } catch (error: any) {
        toast.error(error.message || 'Failed to update cast');
      }
    },
    [selectedCast, updateCastWithImage, updateCastWithJson, handleRefresh]
  );

  const handleDeleteConfirm = useCallback(async () => {
    if (!selectedCast) return;

    try {
      await deleteCast(selectedCast.castId);
      toast.success('Cast deleted successfully');
      setIsDeleteModalOpen(false);
      setSelectedCast(null);
      handleRefresh();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete cast');
    }
  }, [selectedCast, deleteCast, handleRefresh]);

  const columns = useMemo<Column<Cast>[]>(
    () => [
      {
        id: 'profile',
        header: 'Cast',
        accessor: 'name',
        cell: (row) => (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden flex-shrink-0">
              {row.profileImageUrl ? (
                <img
                  src={resolveAssetUrl(row.profileImageUrl)}
                  alt={row.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                  }}
                />
              ) : (
                <span className="text-xs font-semibold text-gray-600">
                  {row.name.charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="min-w-0 text-left">
              <p className="text-sm font-semibold text-gray-900 truncate">{row.name}</p>
              {row.nationality && (
                <p className="text-xs text-gray-500 truncate">{row.nationality}</p>
              )}
              {row.role && (
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  {row.role}
                </p>
              )}
            </div>
          </div>
        ),
        minWidth: '220px',
      },
      {
        id: 'createdAt',
        header: 'Created',
        accessor: 'createdAt',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.createdAt)}
          </span>
        ),
        hideOnMobile: true,
      },
      {
        id: 'updatedAt',
        header: 'Updated',
        accessor: 'updatedAt',
        cell: (row) => (
          <span className="text-sm text-gray-600 text-left">
            {formatDateForDisplay(row.updatedAt)}
          </span>
        ),
        hideOnMobile: true,
      },
    ],
    []
  );

  const actions = useMemo<TableAction<Cast>[]>(
    () => [
      {
        id: 'edit',
        label: 'Edit',
        variant: 'ghost',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
          </svg>
        ),
        onClick: handleEdit,
      },
      {
        id: 'delete',
        label: 'Delete',
        variant: 'danger',
        icon: (
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        ),
        onClick: handleDelete,
      },
    ],
    [handleEdit, handleDelete]
  );

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="text-left">
          <h1 className="text-lg font-bold text-gray-900">Cast Management</h1>
          <p className="text-gray-600 mt-1 pl-3">
            Manage global cast members and their profiles
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3">
          <Button
            onClick={handleCreate}
            leftIcon={
              <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z"
                  clipRule="evenodd"
                />
              </svg>
            }
          >
            Add Cast
          </Button>
        </div>
      </div>

      {/* DataTable */}
      <DataTable<Cast>
        columns={columns}
        fetchData={fetchCasts}
        searchable
        searchPlaceholder="Search casts by name or nationality..."
        initialPageSize={20}
        pageSizeOptions={[10, 20, 50, 100]}
        actions={actions}
        hoverable
        showRefreshButton
        onRefresh={handleRefresh}
        caption="Cast management table"
        key={refreshKeyRef.current}
      />

      {/* Create Cast Modal */}
      <CastFormModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateSubmit}
        mode="create"
      />

      {/* Edit Cast Modal */}
      <CastFormModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setSelectedCast(null);
        }}
        onSubmit={handleEditSubmit}
        mode="edit"
        cast={selectedCast || undefined}
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setSelectedCast(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Cast"
        message={
          selectedCast
            ? `Are you sure you want to delete "${selectedCast.name}"? This action cannot be undone.`
            : 'Are you sure you want to delete this cast member?'
        }
      />
    </div>
  );
};

export default CastManagementPage;


